import { z } from 'zod';

export const LOT_STATUS = ['scheduled', 'live', 'closed', 'cancelled'] as const;
export type LotStatus = (typeof LOT_STATUS)[number];

export const LOT_TIER = ['A', 'B', 'C'] as const;
export type LotTier = (typeof LOT_TIER)[number];

export const LOT_TIER_PRICE_RANGES_CENTS: Record<LotTier, readonly [number, number]> = {
  A: [1_000, 5_000],
  B: [10_000, 30_000],
  C: [50_000, 200_000],
};

export const lotIdSchema = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[a-z0-9_-]+$/i, 'lot id must be url-safe');

export const createLotInputSchema = z
  .object({
    id: lotIdSchema,
    tier: z.enum(LOT_TIER),
    skin_market_name: z.string().min(1).max(200),
    skin_image_url: z.string().url().max(500),
    skin_market_price_cents: z.number().int().positive(),
    skin_asset_id: z.string().min(1).max(64).nullable().optional(),
    scheduled_start_at: z.number().int().positive().describe('unix ms when the lot becomes live'),
    skin_id: z.string().min(1).max(128).nullable().optional(),
    market_hash_name: z.string().min(1).max(200).nullable().optional(),
    skin_name: z.string().min(1).max(200).nullable().optional(),
    image_url: z.string().url().max(500).nullable().optional(),
    rarity_color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/, 'rarity_color must be #RRGGBB')
      .nullable()
      .optional(),
    wear_name: z.string().min(1).max(32).nullable().optional(),
    float_value: z.number().min(0).max(1).nullable().optional(),
    paint_seed: z.number().int().min(0).max(1000).nullable().optional(),
    stickers_json: z.string().max(4000).nullable().optional(),
    keychain_json: z.string().max(2000).nullable().optional(),
    market_price_cents: z.number().int().nonnegative().nullable().optional(),
    last_priced_at: z
      .number()
      .int()
      .positive()
      .describe('unix seconds when market_price_cents was last refreshed')
      .nullable()
      .optional(),
  })
  .refine(
    (input) => {
      const [min, max] = LOT_TIER_PRICE_RANGES_CENTS[input.tier];
      return input.skin_market_price_cents >= min && input.skin_market_price_cents <= max;
    },
    (input) => {
      const [min, max] = LOT_TIER_PRICE_RANGES_CENTS[input.tier];
      return {
        message: `skin_market_price_cents must be within ${min}..${max} for tier ${input.tier}`,
        path: ['skin_market_price_cents'],
      };
    },
  );

export type CreateLotInput = z.infer<typeof createLotInputSchema>;

export const lotPublicSchema = z.object({
  id: z.string(),
  status: z.enum(LOT_STATUS),
  tier: z.enum(LOT_TIER),
  skin_market_name: z.string(),
  skin_image_url: z.string(),
  skin_market_price_cents: z.number().int().positive(),
  scheduled_start_at: z.number().int().positive(),
  started_at: z.number().int().positive().nullable(),
  closed_at: z.number().int().positive().nullable(),
  final_price_cents: z.number().int().nonnegative().nullable(),
  final_bids_count: z.number().int().nonnegative().nullable(),
  final_unique_bidders: z.number().int().nonnegative().nullable(),
  winner_user_id: z.number().int().positive().nullable(),
  winner_first_name: z.string().nullable(),
  winner_username: z.string().nullable(),
  skin_id: z.string().nullable(),
  market_hash_name: z.string().nullable(),
  skin_name: z.string().nullable(),
  image_url: z.string().nullable(),
  rarity_color: z.string().nullable(),
  wear_name: z.string().nullable(),
  market_price_cents: z.number().int().nonnegative().nullable(),
  last_priced_at: z.number().int().positive().nullable(),
  pinned: z.boolean(),
});

export type LotPublic = z.infer<typeof lotPublicSchema>;

export const recentBidderSchema = z.object({
  user_id: z.number().int(),
  first_name: z.string().nullable(),
  username: z.string().nullable(),
  bid_index: z.number().int().positive(),
});

export type RecentBidder = z.infer<typeof recentBidderSchema>;

export const RECENT_BIDDERS_MAX = 5;

export const lotLiveStateSchema = z.object({
  lot_id: z.string(),
  status: z.enum(['live', 'closed']),
  tier: z.enum(LOT_TIER),
  skin_market_name: z.string(),
  skin_image_url: z.string(),
  skin_market_price_cents: z.number().int().positive(),
  current_price_cents: z.number().int().nonnegative(),
  bids_count: z.number().int().nonnegative(),
  last_bidder_user_id: z.number().int().nullable(),
  last_bidder_first_name: z.string().nullable(),
  last_bidder_username: z.string().nullable(),
  last_bidder_user_bid_seq: z.number().int().positive().nullable(),
  last_timer_extension_ms: z.number().int().nonnegative().nullable(),
  recent_bidders: z.array(recentBidderSchema),
  unique_bidders_count: z.number().int().nonnegative(),
  // Liveness signal for hero selection: bids placed in the trailing 60s window.
  // Decays as bids age out, unlike cumulative bids_count.
  recent_bids_60s: z.number().int().nonnegative(),
  started_at: z.number().int().nonnegative(),
  timer_expires_at: z.number().int().nonnegative(),
  closed_at: z.number().int().nonnegative().nullable(),
  winner_user_id: z.number().int().nullable(),
});

export type LotLiveState = z.infer<typeof lotLiveStateSchema>;

export const lotListItemSchema = lotPublicSchema.extend({
  live_state: lotLiveStateSchema.nullable(),
});

export type LotListItem = z.infer<typeof lotListItemSchema>;

export const HERO_REASON = ['pinned', 'fresh_free', 'liveliest', 'scheduled', 'last_win'] as const;
export type HeroReason = (typeof HERO_REASON)[number];

// The home-screen hero is chosen server-side and returned as a self-contained
// object (not a bare id): the rule-5 "last win" candidate is a closed lot, which
// the default lots list (scheduled + live only) does not contain, so the client
// could not resolve it from any bucket it already holds.
export const heroSelectionSchema = z.object({
  reason: z.enum(HERO_REASON),
  lot: lotListItemSchema,
});

export type HeroSelection = z.infer<typeof heroSelectionSchema>;

export const lotsListResponseSchema = z.object({
  lots: z.array(lotListItemSchema),
  hero: heroSelectionSchema.nullable(),
});

export type LotsListResponse = z.infer<typeof lotsListResponseSchema>;

export const recentBidSchema = z.object({
  bid_index: z.number().int().positive(),
  user_id: z.number().int().positive(),
  user_bid_seq: z.number().int().positive().nullable(),
  price_after_cents: z.number().int().nonnegative(),
  placed_at: z.number().int().positive(),
  first_name: z.string().nullable(),
  username: z.string().nullable(),
  timer_extension_ms: z.number().int().nonnegative().nullable(),
});

export type RecentBid = z.infer<typeof recentBidSchema>;

export const lotChatLinkSchema = z.object({
  chat_id: z.number().int(),
  message_thread_id: z.number().int().positive(),
  deep_link: z.string().url(),
  closed_at: z.number().int().positive().nullable(),
});

export type LotChatLink = z.infer<typeof lotChatLinkSchema>;

export const provablyFairSchema = z.object({
  commit: z.string().regex(/^[0-9a-f]{64}$/, 'commit must be 64-char hex'),
  formula: z.string(),
  revealed: z
    .object({
      seed: z.string().regex(/^[0-9a-f]{64}$/, 'seed must be 64-char hex'),
    })
    .nullable(),
});

export type ProvablyFair = z.infer<typeof provablyFairSchema>;

export const bidsPageResponseSchema = z.object({
  bids: z.array(recentBidSchema),
  next_cursor: z.number().int().positive().nullable(),
});

export type BidsPageResponse = z.infer<typeof bidsPageResponseSchema>;

export const lotDetailResponseSchema = z.object({
  lot: lotPublicSchema,
  live_state: lotLiveStateSchema.nullable(),
  recent_bids: z.array(recentBidSchema),
  chat: lotChatLinkSchema.nullable(),
  provably_fair: provablyFairSchema.nullable(),
});

export type LotDetailResponse = z.infer<typeof lotDetailResponseSchema>;

export const RECENT_BIDS_LIMIT = 30;

// Server-authoritative quote for the "spent N bids" disclosure and Buy Now price.
// Client cannot compute these from a paginated bid history (DealDash class action
// 2017 — BIN price misrepresentation; UK ASA anti-pattern #5 — bid-cost disclosure
// must be honest).
export const lotBuyNowQuoteSchema = z.object({
  lot_id: z.string(),
  viewer_bids_count: z.number().int().nonnegative(),
  market_price_cents: z.number().int().nonnegative(),
  buy_now_price_cents: z.number().int().nonnegative(),
  available: z.boolean(),
});

export type LotBuyNowQuote = z.infer<typeof lotBuyNowQuoteSchema>;

// --- History (per-user feed of closed/cancelled lots the viewer bid on) ---

// What happened to the viewer in a finished lot. Server-derived, not client-derived:
// 'cancelled' has no winner and isn't distinguishable from a no-win 'lost' without
// replicating server rules (mirrors the buy-now-quote server-authoritative stance).
export const historyOutcomeSchema = z.enum(['won', 'lost', 'cancelled']);
export type HistoryOutcome = z.infer<typeof historyOutcomeSchema>;

// Prize-delivery state for a WON lot. Until S3's payout-worker exists there is no
// real delivery data — these states are populated by deriveDeliveryStatusStub.
export const historyDeliveryStatusSchema = z.enum([
  'awaiting_trade_offer',
  'trade_sent',
  'delayed_by_win_cap',
]);
export type HistoryDeliveryStatus = z.infer<typeof historyDeliveryStatusSchema>;

export const historyLotItemSchema = lotPublicSchema.extend({
  outcome: historyOutcomeSchema,
  // Bids the viewer placed on this lot — a participation attribute of the lot, not
  // a wallet figure. Always ≥1: the feed only contains lots the viewer bid on.
  // Drives the "you bid ×N" line.
  user_bid_count: z.number().int().positive(),
  // NOTE: delivery_*/buy_now_* are correlated with `outcome` (delivery only on won,
  // buy_now_price only when available). Those invariants are enforced at the single
  // construction site in lots.ts::GET /history, not in the type — deliberate for MVP:
  // this schema is inference-only (the response is never .parse()'d), and a flat
  // shape keeps the wire format and the webapp mapper simple. Revisit as a
  // discriminatedUnion('outcome') if a second producer of this shape appears.
  // BIN still offered to a non-winning participant while the window is open. The
  // indicative price; the authoritative charge still goes through buy-now-quote on
  // LotDetail. Same formula/constant on both sides to avoid a price discrepancy.
  buy_now_available: z.boolean(),
  buy_now_price_cents: z.number().int().nonnegative().nullable(),
  // WON-only; null for lost/cancelled.
  delivery_status: historyDeliveryStatusSchema.nullable(),
  // For delayed_by_win_cap: unix-ms when the hold lifts. Null otherwise.
  delivery_hold_until: z.number().int().positive().nullable(),
});
export type HistoryLotItem = z.infer<typeof historyLotItemSchema>;

export const historyResponseSchema = z.object({
  lots: z.array(historyLotItemSchema),
  // Opaque "<closedAtMs>_<lotId>" cursor; null when no further pages.
  next_cursor: z.string().nullable(),
  // lifetime win count (gates Beginner Auctions at 5) — a game rank, not money.
  lifetime_wins: z.number().int().nonnegative(),
  // total finished lots the viewer participated in (≥ lots.length once paginated).
  total_lots: z.number().int().nonnegative(),
});
export type HistoryResponse = z.infer<typeof historyResponseSchema>;

// Skins worth $500+ carry a 14-day win-cap hold before delivery (finance-model
// win-caps); everything else is awaiting the trade offer right after close, then
// shown as sent once enough time has passed.
const WIN_CAP_HIGH_VALUE_CENTS = 50_000;
const WIN_CAP_HOLD_MS = 14 * 24 * 60 * 60 * 1000;
const TRADE_SENT_AFTER_MS = 3 * 60 * 1000;

export type DeliveryStatusStub = {
  status: HistoryDeliveryStatus;
  holdUntil: number | null;
};

// TODO(S3): replace with real payout-worker delivery state. payout-worker does not
// exist yet, so this derives a plausible delivery status from the skin's value and
// how long ago the lot closed — purely so every WON delivery UI state is reviewable
// end-to-end. Pure and deterministic; the real integration swaps this one function.
export function deriveDeliveryStatusStub(
  marketPriceCents: number,
  closedAtMs: number,
  nowMs: number,
): DeliveryStatusStub {
  if (marketPriceCents >= WIN_CAP_HIGH_VALUE_CENTS) {
    return { status: 'delayed_by_win_cap', holdUntil: closedAtMs + WIN_CAP_HOLD_MS };
  }
  if (nowMs - closedAtMs < TRADE_SENT_AFTER_MS) {
    return { status: 'awaiting_trade_offer', holdUntil: null };
  }
  return { status: 'trade_sent', holdUntil: null };
}
